import React, { useState, useEffect } from "react";
import {
  X,
  Save,
  User,
  BookOpen,
  Users,
  Link2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Hash,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Eye,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";

const ParentForm = ({ parent, onClose, onSubmit, simplified = false, isSubmitting = false }) => {
  const { user } = useAuth();
  const { classes: apiClasses, currentAcademicYear } = useData();

  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    hasWhatsapp: false,
    relation: "Father",
    studentIds: [],
    createdBy: user?._id || user?.id,
  });

  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedStudentForView, setSelectedStudentForView] = useState(null);
  const [isReviewExpanded, setIsReviewExpanded] = useState(true);
  const [studentDetails, setStudentDetails] = useState({});
  const [loadingStudents, setLoadingStudents] = useState(false);

  useEffect(() => {
    if (parent?.studentIds) {
      const details = {};
      parent.studentIds.forEach(s => {
        if (typeof s === 'object' && s._id) {
          details[s._id] = s;
        }
      });
      setStudentDetails(prev => ({ ...prev, ...details }));
    }
  }, [parent]);

  useEffect(() => {
    if (availableStudents.length > 0) {
      const details = {};
      availableStudents.forEach(s => {
        details[s._id] = s;
      });
      setStudentDetails(prev => ({ ...prev, ...details }));
    }
  }, [availableStudents]);

  useEffect(() => {
    if (parent) {
      const studentIds =
        parent.studentIds?.map((s) =>
          typeof s === "string" ? s : s._id || s.id
        ) || [];
      setFormData({
        name: parent.name || "",
        phoneNumber: parent.phoneNumber || "",
        hasWhatsapp: parent.hasWhatsapp || false,
        relation: parent.relation || "Father",
        studentIds,
        createdBy: parent.createdBy || user?._id || user?.id,
      });
      setSelectedStudents(studentIds);
    }
  }, [parent, user]);

  useEffect(() => {
    const fetchStudents = async () => {
      if (selectedUnitId) {
        setLoadingStudents(true);
        try {
          // Fetch students for the selected unit
          const response = await import("../../api/services").then(m => m.studentApi.getStudentsByUnit(selectedUnitId, currentAcademicYear));
          if (response.success) {
            const classStudents = response.data || [];
            const filtered = classStudents.filter((student) => {
              if (parent && parent.studentIds) {
                return !parent.studentIds.some((assigned) => {
                  const id =
                    typeof assigned === "string"
                      ? assigned
                      : assigned._id || assigned.id;
                  return id === student._id;
                });
              }
              return true;
            });
            setAvailableStudents(filtered);
          }
        } catch (error) {
          setAvailableStudents([]);
        } finally {
          setLoadingStudents(false);
        }
      } else {
        setAvailableStudents([]);
      }
    };

    fetchStudents();
  }, [selectedUnitId, parent]);

  const selectedClass = apiClasses?.find((c) => c._id === selectedUnitId);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = "Name required";
      if (!formData.phoneNumber.trim())
        newErrors.phoneNumber = "Phone required";
      if (
        formData.phoneNumber &&
        !/^\+?\d[\d\s\-)]{8,}$/.test(formData.phoneNumber)
      )
        newErrors.phoneNumber = "Invalid phone";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const steps = simplified
    ? [
        { num: 1, label: "Parent Info", icon: User },
      ]
    : [
        { num: 1, label: "Info", icon: User },
        { num: 2, label: "Class", icon: BookOpen },
        { num: 3, label: "Students", icon: Users },
        { num: 4, label: "Review", icon: Link2 },
      ];

  const handleNext = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (validateStep(currentStep)) {
      const nextStep = Math.min(steps.length, currentStep + 1);
      setCurrentStep(nextStep);
    }
  };
  const handlePrev = () => setCurrentStep((prev) => Math.max(1, prev - 1));

  const finalSubmit = async () => {
    // Double-check we're on the final step
    if ((!simplified && currentStep !== 4) || (simplified && currentStep !== 1)) {
      return;
    }
    
    if (!validateStep(1)) return;
    try {
      await onSubmit({
        ...formData,
        _id: parent?._id || parent?.id,
        orgId: user?.orgId,
        studentIds: selectedStudents,
        updatedAt: new Date().toISOString(),
      });
      onClose();
    } catch (err) {
      setErrors({ submit: err.message || "Failed to save" });
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    // Only submit if on final step
    if ((!simplified && currentStep !== 4) || (simplified && currentStep !== 1)) {
      return;
    }
    
    await finalSubmit();
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
        <div className="w-full max-w-3xl overflow-hidden bg-white rounded-lg shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-gray-700" />
              <h2 className="text-base font-bold text-gray-900 uppercase tracking-tight">
                {parent ? "Edit Parent" : "Add Parent Details"}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded transition"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Progress (Only show if not simplified) */}
          {!simplified && (
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
              <div className="flex items-center justify-between">
                {steps.map((step, i) => {
                  const Icon = step.icon;
                  const active = currentStep >= step.num;
                  const done = currentStep > step.num;
                  return (
                    <React.Fragment key={step.num}>
                      <div className="flex items-center">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                            done
                              ? "bg-green-600 text-white"
                              : active
                              ? "bg-primary-600 text-white shadow-md shadow-primary-200"
                              : "bg-gray-200 text-gray-500"
                          }`}
                        >
                          {done ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : (
                            <Icon className="w-3.5 h-3.5" />
                          )}
                        </div>
                        <span
                          className={`ml-2 text-xs font-bold uppercase tracking-tight ${
                            active ? "text-gray-900" : "text-gray-500"
                          }`}
                        >
                          {step.label}
                        </span>
                      </div>
                      {i < steps.length - 1 && (
                        <div
                          className={`w-full h-0.5 mx-4 transition-all duration-500 ${
                            currentStep > step.num
                              ? "bg-green-600"
                              : "bg-gray-200"
                          }`}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            <form
              onSubmit={handleSubmit}
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
            >
              {/* Step 1 */}
              {currentStep === 1 && (
                <div className="space-y-5 duration-300 animate-in fade-in slide-in-from-top">
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                      <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                        {simplified ? "Parent Name" : "Full Name"} *
                      </label>
                      <input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full input-field ${
                          errors.name ? "border-red-500" : ""
                        }`}
                        placeholder="e.g. Ramesh Kumar"
                      />
                      {errors.name && (
                        <p className="mt-1 text-[11px] text-red-600">
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                        {simplified ? "Parent Phone Number" : "Phone Number"} *
                      </label>
                      <input
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        className={`w-full input-field ${
                          errors.phoneNumber ? "border-red-500" : ""
                        }`}
                        placeholder="+91 XXXXX XXXXX"
                      />
                      {errors.phoneNumber && (
                        <p className="mt-1 text-[11px] text-red-600">
                          {errors.phoneNumber}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-6 py-2">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        name="hasWhatsapp"
                        checked={formData.hasWhatsapp}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                      />
                      <span className="text-sm font-bold text-gray-700 group-hover:text-primary-700 transition-colors">
                        Has WhatsApp
                      </span>
                    </label>
                  </div>
                  <div className="max-w-xs">
                    <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                      Relation *
                    </label>
                    <select
                      name="relation"
                      value={formData.relation}
                      onChange={handleInputChange}
                      className="w-full input-field"
                    >
                      <option value="Father">Father</option>
                      <option value="Mother">Mother</option>
                      <option value="Guardian">Guardian</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  {simplified && (
                    <div className="p-4 border border-blue-100 rounded-xl bg-blue-50/50 mt-6 flex items-start gap-3">
                         <span className="text-lg">✨</span>
                         <p className="text-[11px] text-blue-700 leading-relaxed font-medium">
                            <strong>Quick Add:</strong> This parent will be automatically linked to the student you are currently registering. No extra steps needed!
                         </p>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2 - Enhanced Class Selection */}
              {!simplified && currentStep === 2 && (
                <div className="duration-300 animate-in fade-in">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Select Class
                  </label>
                  <select
                    value={selectedUnitId}
                    onChange={(e) => setSelectedUnitId(e.target.value)}
                    className="w-full max-w-md px-4 py-3 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose a class</option>
                    {apiClasses?.map((cls) => (
                      <option key={cls._id} value={cls._id}>
                        {cls.unitName}
                      </option>
                    ))}
                  </select>

                  {selectedClass && (
                    <div className="p-5 mt-5 duration-300 border border-blue-200 rounded-lg bg-blue-50 animate-in slide-in-from-bottom">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-blue-900">
                            {selectedClass.unitName}
                          </h4>
                          <div className="mt-2 space-y-1 text-sm text-blue-800">
                            <p>
                              <strong>Code:</strong> {selectedClass.code}
                            </p>
                            <p>
                              <strong>Teacher:</strong>{" "}
                              {selectedClass.assignedTeachers?.userName ||
                                "Not assigned"}
                            </p>
                            <p>
                              <strong>Email:</strong>{" "}
                              {selectedClass.assignedTeachers?.userEmail || "—"}
                            </p>
                          </div>
                        </div>
                        <BookOpen className="w-8 h-8 text-blue-600 opacity-70" />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3 - Students with Show Button */}
              {!simplified && currentStep === 3 && (
                <div className="space-y-5 duration-300 animate-in fade-in">

                  {parent && selectedStudents.length > 0 && (
                    <div>
                      <p className="mb-2 text-sm font-medium text-gray-700">
                        Currently linked
                      </p>
                      <div className="space-y-2">
                        {selectedStudents.map((id) => {
                          const s = studentDetails[id];
                          if (!s) return null;
                          return (
                            <div
                              key={id}
                              className="flex items-center justify-between p-3 border border-green-200 rounded bg-green-50"
                            >
                              <div>
                                <p className="text-sm font-medium">
                                  {s.firstName} {s.lastName}
                                </p>
                                <p className="text-xs text-gray-600">
                                  ID: {s.studentRegistrationId}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleStudentToggle(id)}
                                className="text-red-600 hover:bg-red-100 p-1.5 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {selectedUnitId ? (
                    loadingStudents ? (
                      <div className="py-12 flex flex-col items-center justify-center text-gray-500 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                        <Loader2 className="w-8 h-8 animate-spin text-primary-500 mb-2" />
                        <p className="text-sm font-medium">Loading students...</p>
                      </div>
                    ) : availableStudents.length > 0 ? (
                      <div className="space-y-3">
                        {availableStudents.map((student) => (
                          <div
                            key={student._id}
                            className={`flex items-center justify-between p-4 border rounded-lg transition-all hover:shadow-sm ${
                              selectedStudents.includes(student._id)
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-300"
                            }`}
                          >
                            <label className="flex items-center flex-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedStudents.includes(student._id)}
                                onChange={() =>
                                  handleStudentToggle(student._id)
                                }
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                              <div className="ml-4">
                                <p className="text-sm font-medium text-gray-900">
                                  {student.firstName} {student.lastName}
                                </p>
                                <div className="flex items-center gap-4 mt-1 text-xs text-gray-600">
                                  <span className="flex items-center gap-1">
                                    <Hash className="w-3.5 h-3.5" />{" "}
                                    {student.studentRegistrationId}
                                  </span>
                                  {student.email && (
                                    <span className="flex items-center gap-1">
                                      <Mail className="w-3.5 h-3.5" />{" "}
                                      {student.email}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </label>
                            <button
                              type="button"
                              onClick={() => setSelectedStudentForView(student)}
                              className="ml-4 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-100 hover:bg-blue-200 rounded transition"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="py-8 text-sm text-center text-gray-500">
                        No unlinked students in this class
                      </p>
                    )
                  ) : (
                    <p className="text-sm text-center text-gray-500">
                      Please select a class first
                    </p>
                  )}
                </div>
              )}

              {/* Step 4 - Review with Expandable */}
              {(currentStep === 4 || (simplified && currentStep === 2)) && (
                <div className="space-y-5 duration-300 animate-in fade-in">
                  <div className="overflow-hidden border rounded-lg">
                    <button
                      type="button"
                      onClick={() => setIsReviewExpanded(!isReviewExpanded)}
                      className="flex items-center justify-between w-full px-5 py-4 text-left transition bg-gray-50 hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5 text-gray-600" />
                        <div>
                          <p className="font-medium text-gray-900">
                            {formData.name}
                          </p>
                          <p className="text-sm text-gray-600">
                            {formData.phoneNumber} • {formData.relation}
                          </p>
                        </div>
                      </div>
                      {isReviewExpanded ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                    <div
                      className={`transition-all duration-300 ${
                        isReviewExpanded
                          ? "max-h-96 opacity-100"
                          : "max-h-0 opacity-0"
                      } overflow-hidden`}
                    >
                      <div className="p-5 bg-white border-t">
                        <p className="text-sm text-gray-700">
                          WhatsApp: {formData.hasWhatsapp ? "Yes" : "No"}
                        </p>
                        <p className="mt-3 text-sm font-medium text-gray-900">
                          Linked Students ({selectedStudents.length})
                        </p>
                        <div className="mt-2 space-y-2">
                          {selectedStudents.length > 0 ? (
                            selectedStudents.map((id) => {
                              const s = studentDetails[id];
                              return s ? (
                                <div
                                  key={id}
                                  className="flex justify-between text-sm text-gray-700"
                                >
                                  <span>
                                    {s.firstName} {s.lastName}
                                  </span>
                                  <span className="text-gray-500">
                                    {s.studentRegistrationId}
                                  </span>
                                </div>
                              ) : null;
                            })
                          ) : (
                            <p className="text-sm italic text-gray-500">None</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-end py-4 mt-8 border-t gap-3 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={currentStep === 1 ? onClose : handlePrev}
                  className="px-6 py-2 text-sm font-bold text-gray-600 transition rounded-lg hover:bg-gray-100"
                >
                  {currentStep === 1 ? "Cancel" : "Back"}
                </button>
                {((!simplified && currentStep === 4) || (simplified && currentStep === 1)) ? (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center justify-center gap-2 px-8 py-2 text-sm font-bold text-white transition bg-primary-600 rounded-lg hover:bg-primary-700 shadow-md shadow-primary-100 disabled:opacity-70 min-w-[140px]"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" /> {parent ? "Update" : "Save & Link"} Parent
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="flex items-center gap-2 px-8 py-2 text-sm font-bold text-white transition bg-primary-600 rounded-lg hover:bg-primary-700 shadow-md shadow-primary-100"
                  >
                    Next Step <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Student Details Dialog */}
      {selectedStudentForView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 duration-200 bg-black/50 backdrop-blur-sm animate-in fade-in">
          <div className="w-full max-w-md p-6 duration-300 bg-white rounded-lg shadow-2xl animate-in zoom-in">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">
                Student Details
              </h3>
              <button
                onClick={() => setSelectedStudentForView(null)}
                className="p-1 rounded hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="font-medium">
                    {selectedStudentForView.firstName}{" "}
                    {selectedStudentForView.lastName}
                  </p>
                  <p className="text-gray-600">
                    {selectedStudentForView.studentRegistrationId}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-500" />{" "}
                <span>{selectedStudentForView.email || "—"}</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-500" />{" "}
                <span>{selectedStudentForView.phoneNumber}</span>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-500" />{" "}
                <span>
                  {new Date(
                    selectedStudentForView.dateOfBirth
                  ).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gray-500" />{" "}
                <span>
                  {selectedStudentForView.address?.city},{" "}
                  {selectedStudentForView.address?.state}
                </span>
              </div>
              <div className="pt-3 border-t">
                <p>
                  <strong>Class:</strong>{" "}
                  {selectedStudentForView.enrollment?.unitName}
                </p>
                <p>
                  <strong>Academic Year:</strong>{" "}
                  {selectedStudentForView.enrollment?.academicYear}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ParentForm;
